import React from "react";

type StudentAnswersInputProps = {
  taskId: number;
  taskType: string;
  questionCount: number;
  value: string;
  onChange: (value: string) => void;
};

const StudentAnswersInput: React.FC<StudentAnswersInputProps> = ({
  taskId,
  taskType,
  questionCount,
  value,
  onChange,
}) => {
  // ✅ Parse existing answers - supports both JSON array and old format
  const [answers, setAnswers] = React.useState<string[]>(() => {
    if (!value || value.trim() === "") {
      return Array(questionCount).fill("");
    }

    try {
      // ✅ NEW: Try parsing as JSON array first
      if (value.trim().startsWith("[")) {
        const parsed = JSON.parse(value);

        // Ensure we have exactly questionCount answers
        while (parsed.length < questionCount) {
          parsed.push("");
        }
        return parsed.slice(0, questionCount);
      }
    } catch (e) {
      // Not JSON, fall through to old format
      console.warn("Failed to parse as JSON, using legacy format");
    }

    // ✅ FALLBACK: Legacy format (for backward compatibility)
    const parsed = value
      .split("\n")
      .map((line) => line.replace(/^\d+\.\s*/, "").trim());

    while (parsed.length < questionCount) {
      parsed.push("");
    }
    return parsed.slice(0, questionCount);
  });

  // ✅ CRITICAL: Update answers array when questionCount changes
  React.useEffect(() => {
    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];

      // If questionCount increased, add empty strings
      while (newAnswers.length < questionCount) {
        newAnswers.push("");
      }

      // If questionCount decreased, trim the array
      if (newAnswers.length > questionCount) {
        return newAnswers.slice(0, questionCount);
      }

      return newAnswers;
    });
  }, [questionCount]);

  // ✅ Update parent with JSON array format
  React.useEffect(() => {
    // Create JSON array of answers
    const answersArray = answers.map((ans) => ans.trim());
    const combined = JSON.stringify(answersArray);

    // Only update if value actually changed
    if (combined !== value) {
      onChange(combined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]); // Only depend on answers array

  const updateAnswer = (idx: number, val: string) => {
    const newAnswers = [...answers];
    newAnswers[idx] = val;
    setAnswers(newAnswers);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-gray-700 mb-2">
        {taskType === "HOREN"
          ? "🎧 Your Listening Answers:"
          : "📝 Fill in the Blanks:"}
      </p>

      {answers.map((answer, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <div className="flex-shrink-0 w-8 h-10 flex items-center justify-center bg-gradient-to-br from-[#009fe3] to-cyan-500 text-white rounded-lg font-bold text-sm shadow-sm">
            {idx + 1}
          </div>
          <input
            type="text"
            value={answer}
            onChange={(e) => updateAnswer(idx, e.target.value)}
            placeholder={`Answer ${idx + 1}`}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
            style={{ fontSize: "16px" }}
            required
          />
        </div>
      ))}

      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          💡 <strong>Tip:</strong> Write your answer in each box. Don't add
          numbering - it's already there!
        </p>
      </div>
    </div>
  );
};

export default StudentAnswersInput;
